import { useEffect, useState } from 'react';
import logoUrl from '../assets/cpe-logo.png';

type Platform = 'ios-safari' | 'ios-other' | 'android' | 'desktop' | 'other';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const SESSION_DISMISS_KEY = 'cpe-install-prompt-dismissed';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
  if (isIOS) {
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isSafari ? 'ios-safari' : 'ios-other';
  }
  if (/Android/.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/.test(ua)) return 'desktop';
  return 'other';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

export function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') return;

    const detected = detectPlatform();
    // The install prompt is only meaningful on mobile — on desktop/laptop
    // installing a PWA isn't the typical flow, so don't nag.
    if (detected !== 'ios-safari' && detected !== 'ios-other' && detected !== 'android') return;

    setPlatform(detected);

    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    const onInstalled = () => {
      setOpen(false);
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    };
    window.addEventListener('appinstalled', onInstalled);

    // Open after first paint so it doesn't flash before the app shell is ready.
    const t = setTimeout(() => setOpen(true), 400);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const onLater = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    setOpen(false);
  };

  const onInstallNative = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setOpen(false);
    }
    setDeferred(null);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Instalar app"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-xl max-h-[95dvh] overflow-y-auto">
        <div className="px-6 pt-7 pb-4 text-center border-b border-slate-100">
          <img src={logoUrl} alt="CPE" className="h-16 w-auto object-contain mx-auto" />
          <h2 className="text-3xl font-bold text-brand-700 mt-3">Créditos</h2>
          <p className="text-base text-slate-500 mt-2">Instala la app en tu teléfono</p>
        </div>

        <div className="px-6 py-6 space-y-5">
          {platform === 'android' && deferred && (
            <button
              type="button"
              onClick={onInstallNative}
              className="w-full py-5 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-xl font-bold rounded-2xl shadow-md flex items-center justify-center gap-2"
            >
              📲 Instalar ahora
            </button>
          )}

          {platform === 'android' && !deferred && <AndroidSteps />}
          {platform === 'ios-safari' && <IOSSafariSteps />}
          {platform === 'ios-other' && <IOSOtherInstructions />}

          <ul className="text-sm text-slate-600 space-y-1.5 bg-slate-50 rounded-xl p-4 border border-slate-200">
            <li>✅ Funciona sin conexión</li>
            <li>🔔 Recibe notificaciones de mensajes</li>
            <li>📱 Pantalla completa, como una app nativa</li>
          </ul>
        </div>

        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={onLater}
            className="w-full px-4 py-3 text-slate-500 hover:text-slate-700 text-base font-medium"
          >
            Más tarde
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon, children }: { n: number; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="shrink-0 w-9 h-9 rounded-full bg-brand-600 text-white text-base font-bold flex items-center justify-center">
        {n}
      </span>
      <span className="shrink-0 w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
        {icon}
      </span>
      <span className="text-base text-slate-700 leading-snug">{children}</span>
    </li>
  );
}

function IOSSafariSteps() {
  return (
    <ol className="space-y-3.5">
      <Step n={1} icon={<ShareIcon />}>Toca el botón <b>Compartir</b> abajo</Step>
      <Step n={2} icon={<AddSquareIcon />}>Desliza y elige <b>Añadir a pantalla de inicio</b></Step>
      <Step n={3} icon={<CheckIcon />}>Toca <b>Añadir</b> arriba a la derecha</Step>
    </ol>
  );
}

function AndroidSteps() {
  return (
    <ol className="space-y-3.5">
      <Step n={1} icon={<KebabIcon />}>Toca el menú <b>⋮</b> de Chrome (arriba a la derecha)</Step>
      <Step n={2} icon={<HomeIcon />}>Elige <b>Añadir a pantalla de inicio</b></Step>
      <Step n={3} icon={<CheckIcon />}>Toca <b>Añadir</b> para confirmar</Step>
    </ol>
  );
}

function IOSOtherInstructions() {
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
    } catch {
      // ignore
    }
  };
  return (
    <div className="space-y-4">
      <p className="text-base text-slate-700 leading-relaxed">
        Para instalar la app en tu iPhone, abre esta página en <b>Safari</b>
        (no funciona desde Chrome u otros navegadores en iOS).
      </p>
      <button
        type="button"
        onClick={copyUrl}
        className="w-full py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-base font-semibold rounded-xl"
      >
        📋 Copiar URL
      </button>
    </div>
  );
}

// Inline SVGs — no extra assets, scale cleanly.

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
    </svg>
  );
}

function AddSquareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M12 8v8" />
      <path d="M8 12h8" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10" />
      <path d="M10 21v-6h4v6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}
